/**
 * Вход по почте и паролю, второй фактор и вход через Яндекс ID.
 *
 * Ответ на неверный пароль и на несуществующий адрес одинаков — иначе форма
 * входа становится способом узнать, кто у нас обслуживается. На рынке, где мы
 * ведём конкурирующие бренды в одной панели, это прямая утечка, поэтому текст
 * ошибки здесь не уточняется намеренно и подсказки «такой почты нет» не будет.
 *
 * Второй фактор — продолжение того же экрана, а не отдельная страница: `login`
 * отвечает `401 mfa_required` и кладёт `mfa_token` в тело проблемы, дальше идёт
 * `POST /auth/login/mfa`. Возврата к паролю не требуется, поэтому и адрес не
 * меняется.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { AuthCard, YandexButton } from './AuthCard';
import { Field } from '../../ui/form';
import { ErrorNotice } from '../../ui/state';
import { login, loginMfa, startYandexLogin, type Session } from '../../api/auth';
import { ApiError, extra, fieldErrors } from '../../api/problem';
import { safePath } from '../../return-to';
import { LEGAL, LEGAL_LINK_PROPS } from '../../legal';

type Step =
  | { kind: 'password' }
  /** Пароль принят, ждём код. Токен второго фактора живёт только здесь. */
  | { kind: 'mfa'; token: string };

export function Login({ onDone }: { onDone: (session: Session) => void }) {
  const location = useLocation();
  const [params] = useSearchParams();

  const [step, setStep] = useState<Step>({ kind: 'password' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  // Контракт требует явного значения. Спрашиваем, а не решаем молча: на общем
  // компьютере запомненное устройство — это чужой доступ к данным организации.
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  // Форма пароля сменилась формой кода — фокус нужно перенести руками, иначе он
  // остаётся на исчезнувшей кнопке и человек с клавиатуры оказывается в никуда.
  // Это перенос фокуса после перехода, а не `autofocus` при загрузке страницы:
  // второе уводит от начала страницы того, кто её ещё не прочитал.
  const codeField = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (step.kind === 'mfa') codeField.current?.focus();
  }, [step.kind]);

  // Куда вернуть после входа. Только путь внутри кабинета, и проверяет это
  // общий `safePath`: здесь стояло `startsWith('/')`, которое пропускает
  // протокол-относительный `//evil.example/` — открытый редирект ровно в тот
  // момент, когда человек только что ввёл пароль на настоящем домене.
  const from = (location.state as { from?: string } | null)?.from;
  const returnTo = safePath(from);

  // Провайдер вернул человека с ошибкой — показываем её, а не молчим.
  const providerError = params.get('error');

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      onDone(await login({ email, password, remember_device: remember }));
    } catch (caught) {
      if (caught instanceof ApiError) {
        const token = extra(caught.problem, 'mfa_token');
        if (caught.code === 'mfa_required' && token) {
          setStep({ kind: 'mfa', token });
          setBusy(false);
          return;
        }
        setFields(fieldErrors(caught.problem));
      }
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(event: FormEvent) {
    event.preventDefault();
    if (step.kind !== 'mfa') return;
    setBusy(true);
    setError(null);
    setFields({});
    try {
      // Шесть цифр — код приложения; всё прочее считаем кодом восстановления.
      const digits = /^[0-9]{6}$/.test(code.trim());
      onDone(
        await loginMfa({
          mfa_token: step.token,
          ...(digits ? { totp_code: code.trim() } : { recovery_code: code.trim() }),
        }),
      );
    } catch (caught) {
      if (caught instanceof ApiError) setFields(fieldErrors(caught.problem));
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (step.kind === 'mfa') {
    return (
      <AuthCard
        title="Подтвердите вход"
        lead="Введите код из приложения-аутентификатора. Если приложения под рукой нет — подойдёт код восстановления."
      >
        {error ? <ErrorNotice error={error} /> : null}

        <form onSubmit={submitMfa} noValidate>
          <Field
            id="mfa-code"
            label="Код"
            hint="Шесть цифр из приложения или код восстановления"
            error={fields.totp_code ?? fields.recovery_code}
          >
            {(props) => (
              <input
                {...props}
                ref={codeField}
                type="text"
                name="totp_code"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            )}
          </Field>

          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? 'Проверяем…' : 'Подтвердить'}
          </button>
        </form>

        <div className="auth-foot">
          <button
            className="linkish"
            type="button"
            onClick={() => {
              setStep({ kind: 'password' });
              setError(null);
              setCode('');
            }}
          >
            Ввести пароль заново
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Вход в кабинет"
      footer={
        // Здесь стояло «Начните с бесплатного аудита на seeno.ru», и это
        // замыкало кольцо: аудит вёл обратно на вход, а завести учётную запись
        // было нечем. Теперь названы обе двери — регистрация и аудит, — и обе
        // ведут в существующие экраны.
        // Моно тут не годится: это предложение, а не подпись и не показание
        // (закон 2 — foundations.md). `.auth-fine` даёт мелкий кегль основной
        // гарнитурой и подчёркнутые ссылки, то есть ровно то, чем эта строка
        // и является.
        // Куда человек шёл, переносится и на регистрацию: на вход его чаще
        // всего привела кнопка «забрать отчёт», а у нового клиента учётной
        // записи ещё нет. Без переноса намерение терялось ровно на том
        // переходе, который новый клиент и делает.
        <p className="auth-fine">
          Нет учётной записи?{' '}
          <Link to="/auth/signup" state={from ? { from } : undefined}>
            Зарегистрируйтесь
          </Link>{' '}
          или начните с <Link to="/audit">бесплатного аудита</Link>.
        </p>
      }
    >
      {providerError ? (
        <div className="notice" role="alert">
          <b>Войти через Яндекс ID не получилось</b>
          Попробуйте ещё раз или войдите по почте и паролю.
        </div>
      ) : null}

      {error ? <ErrorNotice error={error} /> : null}

      <form onSubmit={submitPassword} noValidate>
        <Field
          id="email"
          label="Почта"
          hint="Та почта, на которую оформлена подписка"
          error={fields.email}
        >
          {(props) => (
            <input
              {...props}
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        {/* Подсказки нет намеренно: здесь вводят СУЩЕСТВУЮЩИЙ пароль, и
            требование «не меньше 12 знаков» на входе бесполезно — оно про
            задание пароля, а не про его ввод. Пустая строка место под полем
            всё равно держит, чтобы поле не прыгало, когда появится ошибка. */}
        <Field id="password" label="Пароль" hint="" error={fields.password}>
          {(props) => (
            <input
              {...props}
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <label className="consent">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <span>Запомнить это устройство</span>
        </label>

        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Проверяем…' : 'Войти'}
        </button>
      </form>

      <YandexButton
        onClick={() => {
          setError(null);
          // Отказ старта OAuth остаётся НА ЭТОМ экране: раньше человека уводило
          // полной навигацией, и любой отказ показывался голым problem+json в
          // окне браузера — без оформления и без кнопки назад.
          void startYandexLogin(returnTo).catch(setError);
        }}
        disabled={busy}
      />

      <p className="micro auth-forgot">
        <Link to="/auth/forgot">Забыли пароль?</Link>
      </p>

      {/* Вход через провайдера — не всегда вход: контракт заводит отдельную
          учётную запись, если Яндекс ID не подтвердил адрес. То есть эта кнопка
          умеет создавать запись, а галочки согласия на ней нет и быть не может
          (человек уходит на чужой домен раньше формы). Пока это так, экран
          обязан хотя бы назвать документы и сказать, что данные придут от
          провайдера, — молчание здесь означало бы, что запись заводится вовсе
          без упоминания обработки.

          Стоит НИЖЕ «Забыли пароль?»: та ссылка относится к форме с паролем, и
          вклиниваться между ними абзацем про чужого провайдера значит разорвать
          пару «поле пароля — восстановление пароля». */}
      <p className="auth-fine">
        При входе через Яндекс ID мы получаем от провайдера ваш адрес почты и имя.
        Обработка — по{' '}
        <a href={LEGAL.privacy} {...LEGAL_LINK_PROPS}>
          политике конфиденциальности
        </a>
        , условия услуг — в{' '}
        <a href={LEGAL.offer} {...LEGAL_LINK_PROPS}>
          оферте
        </a>
        .
      </p>
    </AuthCard>
  );
}
